import {
    Flex,
    Heading,
    Skeleton,
    ThemeFontSizeVariable,
    ThemeRadiusVariable,
    ThemeSpaceVariable,
    useDisclosure,
} from "@caspeco/casper-ui-library";
import { useExternalBookingSettings } from "api/api-hooks/use-external-booking-settings";
import { useFinalizeBooking } from "api/api-hooks/use-finalize-booking";
import { usePaymentTerminal } from "api/api-hooks/use-payment-terminal";
import { useWebBooking } from "api/api-hooks/use-web-booking";
import { BookingAlertManager } from "components/alerts/booking-alert-manager";
import BookingConditions from "components/details-form/booking-conditions";
import { PhoneField } from "components/details-form/phone-field";
import { WaitListStartTimes } from "components/details-form/wait-list-start-times";
import { PaymentMethodField } from "components/payment-field/payment-field";
import { PrivacyPolicyModal } from "components/privacy-policy-modal";
import { hasFlag } from "helpers/binary-flag-helper";
import { getDefaultPaymentMethod } from "helpers/payment-helper";
import { useBookingStateContext } from "hooks/use-booking-state";
import { usePaymentRule } from "hooks/use-payment-rule";
import { useStepTracking } from "logger/use-step-tracking";
import { usePageTracking } from "logger/use-page-tracking";
import { PageType } from "logger/page-tracking";
import { TrackingStepName } from "logger/user-tracking";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { getSearchParams } from "search-params";
import { ContactInfoForm, State } from "store/store-types";
import { Contact } from "types/contact";
import { BookingWebSettingsFlag } from "types/external-booking-settings";
import { PaymentRule, PaymentMethod } from "types/payment";
import CustomerFields from "components/details-form/customer-fields";
import EmailField from "components/details-form/email-field";
import MessageField from "components/details-form/message-field";
import NameField from "components/details-form/name-field";
import NewsletterField from "components/details-form/newsletter-field";
import PrivacyPolicyText from "components/details-form/privacy-policy-text";
import SubmitButton from "components/details-form/submit-button";
import { Articles } from "components/my-booking/articles";
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import { TwoFactorModal } from "components/details-form/two-factor-modal";
import { TwoFactorChallenge, TwoFactorRequiredError } from "types/two-factor";

export const DetailsWithPaymentForm = () => {
    const { t } = useTranslation();
    const { isOpen, onClose, onOpen } = useDisclosure();
    const { data: settings } = useExternalBookingSettings();
    const state = useBookingStateContext();
    const finalize = useFinalizeBooking();

    const terminal = usePaymentTerminal();
    const paymentRule = usePaymentRule();
    const { data: booking } = useWebBooking();

    // Track details page
    useStepTracking({
        stepName: TrackingStepName.DetailsPage,
        autoTrack: true,
    });

    const contact = (state.contact || {}) as Contact;

    const isPrepayment = paymentRule === PaymentRule.Prepayment;
    const isNoShow = paymentRule === PaymentRule.NoShowFee;

    const displayMessageBox = settings && settings.displayMessageBox;
    const unitMetaData = settings?.unitsMetaData;
    const showCustomerFields = hasFlag(
        unitMetaData?.binaryFlags ?? 0,
        BookingWebSettingsFlag.displayCustomerName
    );

    function isReepayPaymentMethodConfigured(): boolean {
        return (settings?.paymentMethods ?? []).some((method) => method === PaymentMethod.Reepay);
    }

    usePageTracking({
        pageName: "DetailsWithPaymentForm",
        pageType: PageType.Page,
        metadata: {
            isPrepayment,
            isNoShow,
            isWaitList: Boolean(booking?.isWaitList()),
            displayMessageBox: Boolean(displayMessageBox),
            showCustomerFields,
        },
    });

    const { control, handleSubmit, formState, trigger, watch, setValue } = useForm<ContactInfoForm>(
        {
            mode: "onBlur",
            defaultValues: {
                ...contact,
                payMethod: settings
                    ? getDefaultPaymentMethod(settings, isPrepayment, state)
                    : undefined,
            },
        }
    );
    const hasReepayPaymentMethod = isReepayPaymentMethodConfigured();

    useEffect(() => {
        const defaultPayMethod = hasReepayPaymentMethod
            ? PaymentMethod.Reepay
            : settings
              ? getDefaultPaymentMethod(settings, isPrepayment, state)
              : undefined;
        setValue("payMethod", defaultPayMethod ?? null, {
            shouldValidate: true,
            shouldDirty: false,
        });
    }, [booking, isPrepayment, settings, state, setValue, hasReepayPaymentMethod]);

    const turnstileRef = useRef<TurnstileInstance>(null);
    const [twoFactorChallenge, setTwoFactorChallenge] = useState<TwoFactorChallenge | null>(null);
    // The state the guest actually submitted, Turnstile token included. Follow-up calls carrying
    // the code have to send the same payload; rebuilding from context would silently drop the token.
    const submittedState = useRef<State | null>(null);

    /**
     * The api asks for an sms code when it suspects the traffic is not a real guest. The booking
     * stays held meanwhile, so this reopens the code prompt instead of failing the booking.
     */
    const finalizeWithTwoFactorHandling = (
        state: State,
        twoFactor?: { code?: string; resend?: boolean }
    ) => {
        finalize.mutate(
            {
                ...state,
                twoFactorCode: twoFactor?.code,
                resendTwoFactorCode: twoFactor?.resend,
            },
            {
                onError: (error) => {
                    if (error instanceof TwoFactorRequiredError) {
                        setTwoFactorChallenge(error.challenge);
                    }
                },
                onSuccess: () => setTwoFactorChallenge(null),
            }
        );
    };

    const onSubmit = () => {
        let token = "";
        if (turnstileRef.current) {
            token = turnstileRef.current.getResponse() ?? "";
        }
        const stateWithToken = {
            ...state,
            turnstileToken: token,
        };

        submittedState.current = stateWithToken;

        if (booking?.isWaitList() || (!isPrepayment && !isNoShow)) {
            finalizeWithTwoFactorHandling(stateWithToken);
        } else {
            const terminalState = {
                ...stateWithToken,
                payment: {
                    payMethod: watch("payMethod"),
                },
            };
            terminal.mutate({ state: terminalState, isNoShow });
        }
    };
    const { withPayment } = getSearchParams();
    return (
        <form
            onSubmit={(e) => {
                void handleSubmit(onSubmit)(e);
            }}
            noValidate
        >
            {twoFactorChallenge && (
                <TwoFactorModal
                    challenge={twoFactorChallenge}
                    isSubmitting={finalize.isPending}
                    onSubmitCode={(code) =>
                        finalizeWithTwoFactorHandling(submittedState.current ?? state, { code })
                    }
                    onResend={() =>
                        finalizeWithTwoFactorHandling(submittedState.current ?? state, {
                            resend: true,
                        })
                    }
                    onClose={() => setTwoFactorChallenge(null)}
                />
            )}

            {/* Invisible Turnstile container */}
            <Turnstile
                siteKey={import.meta.env.VITE_APP_TURNSTILE_SITE_KEY as string}
                options={{ size: "invisible" }}
                ref={turnstileRef}
            />
            <Flex direction="column" gap={ThemeSpaceVariable.Large} px={ThemeSpaceVariable.Medium}>
                {!booking?.isWaitList() && <Articles />}
                <Flex direction="column" gap={ThemeSpaceVariable.Medium}>
                    {booking?.isWaitList() && <WaitListStartTimes />}
                    <Heading as="h5" fontWeight="500" fontSize={ThemeFontSizeVariable.Large}>
                        {t("contactDetails")}
                    </Heading>
                    <NameField
                        value={contact.fname || ""}
                        control={control}
                        variant="fname"
                        // disabled={!!contactLoadedByFriendlyId?.fname}
                        trigger={trigger}
                    />
                    <NameField
                        value={contact.lname || ""}
                        control={control}
                        variant="lname"
                        // disabled={!!contactLoadedByFriendlyId?.lname}
                        trigger={trigger}
                    />
                    <PhoneField
                        value={contact.mobile || ""}
                        control={control}
                        // disabled={!!contactLoadedByFriendlyId?.mobile}
                        trigger={trigger}
                    />
                    <EmailField
                        value={contact.email || ""}
                        control={control}
                        // disabled={!!contactLoadedByFriendlyId?.email}
                        trigger={trigger}
                    />

                    {showCustomerFields && <CustomerFields control={control} />}

                    {displayMessageBox && (
                        <MessageField control={control} value={contact.message || ""} />
                    )}

                    {settings?.newsletter && <NewsletterField />}
                </Flex>

                {!booking && withPayment ? (
                    <Skeleton w="full" height="4.5rem" borderRadius={ThemeRadiusVariable.Small} />
                ) : (
                    <BookingAlertManager
                        isNoShow={isNoShow}
                        isPrepayment={isPrepayment}
                        isWaitList={!!booking?.isWaitList()}
                        hasNoShowCharge={false}
                        isBookingCanceled={false}
                        shouldShowConfirmWaitListBtn={false}
                        detailsForm
                    />
                )}

                {isPrepayment && !booking?.isWaitList() && !hasReepayPaymentMethod && (
                    <PaymentMethodField control={control} trigger={trigger} />
                )}
                <BookingConditions />
                <Flex direction="column" gap={ThemeSpaceVariable.Medium}>
                    {!booking && withPayment ? (
                        <Skeleton
                            w="full"
                            height="3.5rem"
                            borderRadius={ThemeRadiusVariable.XLarge}
                        />
                    ) : (
                        <SubmitButton
                            isLoading={finalize.isPending || terminal?.isPending}
                            isDisabled={!formState.isValid}
                            trackingId="finalize-booking"
                        />
                    )}
                    <PrivacyPolicyText onOpen={onOpen} />
                </Flex>
            </Flex>
            <PrivacyPolicyModal isOpen={isOpen} onClose={onClose} />
        </form>
    );
};
